// src/components/Sidebar.jsx
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import { useAuthService } from "../../../services/authService";
import { SwapOutlined } from "@ant-design/icons";

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const { pathname } = location;
  const { user } = useAuth();

  const trigger = useRef(null);
  const sidebar = useRef(null);
  const { userLogout } = useAuthService();

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === "true"
  );

  // Schließen bei Klick außerhalb
  useEffect(() => {
    const clickHandler = (event) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(event.target) ||
        trigger.current.contains(event.target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [sidebarOpen, setSidebarOpen]);

  // Schließen bei ESC-Taste
  useEffect(() => {
    const keyHandler = (event) => {
      if (!sidebarOpen || event.key !== "Escape") return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [sidebarOpen, setSidebarOpen]);

  // Sidebar-Zustand speichern und Klassen am Body verwalten
  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded.toString());
    if (sidebarExpanded) {
      document.body.classList.add("sidebar-expanded");
    } else {
      document.body.classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  return (
    <>
      {/* Overlay für mobile Geräte */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
      <aside
        ref={sidebar}
        className={`fixed inset-y-0 left-0 z-50 w-64 overflow-y-auto bg-white border-r transition-transform duration-300 ease-in-out transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static lg:flex flex-col`}
      >
        {/* SIDEBAR HEADER */}
        <div className="flex items-center justify-between p-4 border-b">
          <NavLink to="/">
            <img src="/rdy.png" alt="Logo" className="h-8" />
          </NavLink>
          <button
            ref={trigger}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
            className="lg:hidden focus:outline-none"
          >
            {/* Hamburger-Menü-Icon aus Heroicons */}
            {sidebarOpen ? (
              <svg
                className="h-6 w-6 text-gray-700"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="h-6 w-6 text-gray-700"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>
        {/* SIDEBAR MENU */}
        <nav className="flex-1 p-4 flex flex-col mt-4 md:mt-0">
          <ul className="space-y-4 flex-grow">
            {/* Dashboard */}
            <li>
              <NavLink
                to="/admindashboard"
                end
                className={({ isActive }) =>
                  `flex items-center p-2 text-base font-medium rounded-lg transition-colors ${
                    isActive
                      ? "bg-orange-500 text-white"
                      : "text-gray-700 hover:bg-orange-100"
                  }`
                }
              >
                {/* Optional: SVG Icon */}
                <svg
                  className="mr-3 h-6 w-6 text-gray-700"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M10 20a10 10 0 100-20 10 10 0 000 20zM9 9h2v6H9V9zm0-4h2v2H9V5z" />
                </svg>
                Admin Dashboard
              </NavLink>
            </li>

            {/* QR-Management */}
            <li>
              <NavLink
                to="/admindashboard/user"
                className={({ isActive }) =>
                  `flex items-center p-2 text-base font-medium rounded-lg transition-colors ${
                    isActive
                      ? "bg-orange-500 text-white"
                      : "text-gray-700 hover:bg-orange-100"
                  }`
                }
              >
                <svg
                  className="mr-3 h-6 w-6 text-gray-700"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Beispiel-QR-Code-Icon */}
                  <path d="M5 5h2v2H5V5zm4 0h2v2H9V5zm4 0h2v2h-2V5zM5 9h2v2H5V9zm4 0h2v2H9V9zm4 0h2v2h-2V9zM5 13h2v2H5v-2zm4 0h2v2H9v-2zm4 0h2v2h-2v-2z" />
                </svg>
                User
              </NavLink>
            </li>

            {/*<li>
              <NavLink
                to="/dashboard/voucher-management"
                className={({ isActive }) =>
                  `flex items-center p-2 text-base font-medium rounded-lg transition-colors ${
                    isActive
                      ? "bg-orange-500 text-white"
                      : "text-gray-700 hover:bg-orange-100"
                  }`
                }
              >
                <svg
                  className="mr-3 h-6 w-6 text-gray-700"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                 
                  <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2H9l-7-7V5z" />
                </svg>
                Gutschein-Verwaltung
              </NavLink>
            </li>*/}

            {/* Berichte & Analysen */}
            <li>
              <NavLink
                to="/admindashboard/support"
                className={({ isActive }) =>
                  `flex items-center p-2 text-base font-medium rounded-lg transition-colors ${
                    isActive
                      ? "bg-orange-500 text-white"
                      : "text-gray-700 hover:bg-orange-100"
                  }`
                }
              >
                <svg
                  className="mr-3 h-6 w-6 text-gray-700"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Beispiel-Bericht-Icon */}
                  <path d="M4 3h12v2H4V3zm0 4h12v2H4V7zm0 4h12v2H4v-2zm0 4h12v2H4v-2z" />
                </svg>
                Support
              </NavLink>
            </li>

            {user.roles.includes("admin") && (
              <li>
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) =>
                    `flex items-center p-2 text-base font-medium rounded-lg transition-colors ${
                      isActive
                        ? "bg-orange-500 text-white"
                        : "text-gray-700 hover:bg-orange-100"
                    }`
                  }
                >
                  <SwapOutlined className="mr-3 h-6 w-6 text-gray-700" />
                  Nutzer Dashboard
                </NavLink>
              </li>
            )}
          </ul>

          {/* OTHERS GROUP (Settings und Logout) */}
          <div className="mt-auto">
            <h3 className="px-2 text-sm font-semibold text-gray-500 uppercase">
              Others
            </h3>
            <ul className="mt-4 space-y-4">
              {/* Settings */}
              <li>
                <NavLink
                  to="/dashboard/settings"
                  className={({ isActive }) =>
                    `flex items-center p-2 text-base font-medium rounded-lg transition-colors ${
                      isActive
                        ? "bg-orange-500 text-white"
                        : "text-gray-700 hover:bg-orange-100"
                    }`
                  }
                >
                  <svg
                    className="mr-3 h-6 w-6 text-gray-700"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {/* Beispiel-Einstellungen-Icon */}
                    <path d="M10 2a1 1 0 011 1v2a1 1 0 11-2 0V3a1 1 0 011-1zm4.22 2.22a1 1 0 011.42 1.42l-1.06 1.06a1 1 0 11-1.42-1.42l1.06-1.06zM18 10a1 1 0 011 1v4a1 1 0 11-2 0v-4a1 1 0 011-1zM14.22 14.22a1 1 0 01-1.42 1.42l-1.06-1.06a1 1 0 011.42-1.42l1.06 1.06zM10 16a1 1 0 011 1v2a1 1 0 11-2 0v-2a1 1 0 011-1zm-4.22-1.78a1 1 0 01-1.42-1.42l1.06-1.06a1 1 0 011.42 1.42l-1.06 1.06zM2 10a1 1 0 011-1h2a1 1 0 110 2H3a1 1 0 01-1-1zm4.22-4.22a1 1 0 011.42-1.42l1.06 1.06a1 1 0 11-1.42 1.42L6.22 5.78z" />
                  </svg>
                  Settings
                </NavLink>
              </li>

              {/* Logout */}
              <li>
                <button
                  onClick={userLogout}
                  className="flex items-center p-2 text-base font-medium rounded-lg transition-colors text-gray-700 hover:bg-orange-100 w-full"
                >
                  <svg
                    className="mr-3 h-6 w-6 text-gray-700"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {/* Beispiel-Logout-Icon */}
                    <path d="M3 4a1 1 0 011-1h9a1 1 0 110 2H5.414l4.293 4.293a1 1 0 11-1.414 1.414L3 6.414V14a1 1 0 11-2 0V4zM19 10a1 1 0 011 1v4a1 1 0 11-2 0v-4a1 1 0 011-1z" />
                  </svg>
                  Logout
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
